/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, FC, useRef, useEffect } from "react";
import "../css/MonthYearPicker.css";
import dropdownIcon from "../../assets/images/month-year-picker-dropdown.svg";

interface MonthYearPickerProps {
  selectedDate: Date;
  setSelectedDate: (date: string) => void;
  setCurrentDate: boolean;
}

const months: string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const years: number[] = Array.from({ length: 100 }, (_, i) => 2000 + i);
const MonthYearPicker: FC<MonthYearPickerProps> = ({
  selectedDate,
  setSelectedDate,
  setCurrentDate,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    selectedDate.getMonth()
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    selectedDate.getFullYear()
  );
  const pickerRef = useRef<HTMLDivElement>(null);

  // Calculate allowed months and years within the next 30 days
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const endDate = new Date(now);
  endDate.setDate(now.getDate() + 29);

  const allowedMonths: { month: number; year: number }[] = [];
  const allowedYears: number[] = [];

  let temp = new Date(now);
  while (temp <= endDate) {
    if (!allowedYears.includes(temp.getFullYear())) {
      allowedYears.push(temp.getFullYear());
    }
    if (
      !allowedMonths.some(
        (m) => m.month === temp.getMonth() && m.year === temp.getFullYear()
      )
    ) {
      allowedMonths.push({ month: temp.getMonth(), year: temp.getFullYear() });
    }
    temp.setDate(temp.getDate() + 1);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const now = new Date();
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
    setSelectedDate(now.toISOString());
  }, [setCurrentDate]);
  
  const togglePicker = (): void => setOpen(!open);

  const handleMonthSelect = (index: number): void => {
    setSelectedMonth(index);
    const now = new Date();
    let newDate;
    if (selectedYear === now.getFullYear() && index === now.getMonth()) {
      newDate = now;
    } else {
      newDate = new Date(selectedYear, index, 1);
    }
    setSelectedDate(newDate.toISOString());
    setOpen(false);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const now = new Date();
    let newDate;
    if (year === now.getFullYear() && selectedMonth === now.getMonth()) {
      newDate = now;
    } else {
      newDate = new Date(year, selectedMonth, 1);
    }
    setSelectedDate(newDate.toISOString());
  };

  return (
    <div className="month-year-picker-container" ref={pickerRef}>
      <div className="month-year-picker-button" onClick={togglePicker}>
        <div>
          {months[selectedMonth]} {selectedYear}
        </div>
        <img src={dropdownIcon} alt="dropdown-logo" />
      </div>

      {open && (
        <div className="month-year-picker-dropdown">
          <select
            className="year-selector"
            value={selectedYear}
            onChange={handleYearChange}
          >
            {years.map((year: number) => (
              <option
                key={year}
                value={year}
                disabled={!allowedYears.includes(year)}
              >
                {year}
              </option>
            ))}
          </select>

          <div className="month-grid">
            {months.map((month: string, index: number) => {
              const isAllowed = allowedMonths.some(
                (m) => m.month === index && m.year === selectedYear
              );
              return (
                <div
                  key={month}
                  className={`month-cell ${
                    index === selectedMonth ? "selected" : ""
                  } ${!isAllowed ? "disabled" : ""}`}
                  onClick={() => isAllowed && handleMonthSelect(index)}
                  style={
                    !isAllowed ? { pointerEvents: "none", opacity: 0.5 } : {}
                  }
                >
                  {month}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
